import { dashboardService } from '@/services';
import { Download, FileSpreadsheet, Info, Users } from 'lucide-react';
import { useState } from 'react';

interface ReporteCard {
  id: string;
  titulo: string;
  descripcion: string;
  icono: React.ReactNode;
  colorClase: string;
  nombreArchivo: () => string;
  descargar: () => Promise<Blob>;
}

const REPORTES: ReporteCard[] = [
  {
    id: 'consolidado',
    titulo: 'Informe Consolidado',
    descripcion:
      'Exporta el listado completo de empresas con sus horas de asesoría, porcentaje de avance y desglose detallado de cumplimiento por norma NTC. Incluye fórmulas de Excel y promedios automáticos.',
    icono: <FileSpreadsheet className="h-4 w-4" />,
    colorClase: 'bg-(--admin-blue-bg) text-(--admin-blue)',
    nombreArchivo: () => `reporte_empresas_${new Date().toISOString().split('T')[0]}.xlsx`,
    descargar: () => dashboardService.downloadExcelReport(),
  },
  {
    id: 'por-asesor',
    titulo: 'Consolidado por Asesor',
    descripcion:
      'Una hoja por asesor con las empresas que maneja o manejó. Por cada asesoría muestra horas y estado, marcando cuáles son de otro asesor tras una reasignación. Separa las horas propias de las de la empresa completa, en cinco categorías: aprobadas, rechazadas, pendientes de revisar, pendientes de confirmar y en curso.',
    icono: <Users className="h-4 w-4" />,
    colorClase: 'bg-(--admin-purple-bg) text-(--admin-purple)',
    nombreArchivo: () => `consolidado_por_asesor_${new Date().toISOString().split('T')[0]}.xlsx`,
    descargar: () => dashboardService.downloadConsolidadoPorAsesorReport(),
  },
];

export function AdminReportesPage() {
  // Se guarda el id del reporte en curso (no un booleano) para que descargar uno no deshabilite
  // el botón del otro.
  const [exportandoId, setExportandoId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async (reporte: ReporteCard) => {
    setExportandoId(reporte.id);
    setError(null);
    try {
      const blob = await reporte.descargar();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', reporte.nombreArchivo());
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al exportar:', err);
      setError(`No se pudo generar "${reporte.titulo}". Intenta de nuevo.`);
    } finally {
      setExportandoId(null);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-400">
      <div className="flex flex-col gap-1.5 mb-8">
        <h1 className="text-[22px] font-semibold tracking-tight text-(--admin-text)">Reportes y Exportación</h1>
        <p className="text-[13px] text-(--admin-text3)">
          Genera y descarga informes detallados sobre el progreso del proyecto y cumplimiento de las empresas.
        </p>
      </div>

      {error && (
        <div className="mb-5 rounded-lg border border-(--admin-red) bg-(--admin-red-bg) px-4 py-3 text-[13px] text-(--admin-red)">
          {error}
        </div>
      )}

      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {REPORTES.map((reporte) => {
          const exportando = exportandoId === reporte.id;
          return (
            <div key={reporte.id} className="admin-card overflow-hidden flex flex-col h-full">
              <div className="admin-card-header bg-(--admin-surface2)/30 border-b border-(--admin-border)">
                <div className="flex items-center gap-2.5">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${reporte.colorClase}`}>{reporte.icono}</div>
                  <div className="text-[13px] font-semibold text-(--admin-text)">{reporte.titulo}</div>
                </div>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-[13px] leading-relaxed text-(--admin-text2) mb-6">{reporte.descripcion}</p>

                <div className="mt-auto space-y-4">
                  <div className="flex items-center justify-between py-2.5 border-y border-(--admin-border) border-dashed">
                    <div className="flex items-center gap-2 text-[11px] text-(--admin-text3) font-medium uppercase tracking-wider">
                      <Info className="h-3 w-3" />
                      Formato de salida
                    </div>
                    <div className="text-[12px] font-mono font-medium text-(--admin-text2)">.XLSX</div>
                  </div>

                  <button
                    onClick={() => handleExport(reporte)}
                    disabled={exportando}
                    className={`admin-btn w-full py-2.5 flex items-center justify-center gap-2 ${
                      exportando ? 'opacity-70' : 'bg-(--admin-text) text-white hover:bg-black'
                    }`}
                  >
                    {exportando ? (
                      <>
                        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        <span>Generando...</span>
                      </>
                    ) : (
                      <>
                        <Download className="h-3.5 w-3.5" />
                        <span>Descargar Excel</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
